/* @flow */
import PropTypes from 'prop-types';
import * as React from 'react';
import invariant from 'invariant';

import Button from 'components/Button.jsx';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupName from 'groups/components/GroupName.jsx';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupStore from 'groups/stores/GroupStore';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Menu from 'components/Menu.jsx';
import MenuItem from 'components/MenuItem.jsx';
import Message from 'entities/Message';
import Text from 'components/Text.jsx';
import Time from 'components/Time.jsx';
import Timestamp from 'components/Timestamp.jsx';

import './styles/GroupMessageInformation.scss';

type Props = {
  showReadReceipt?: boolean,
  message: Message,
};

export default class GroupMessageInformation extends React.Component<Props> {
  static contextTypes = {
    group: PropTypes.instanceOf(Group),
  };

  /**
   * Renders a label and dropdown with information about message recipients (threads).
   */
  renderRecipients() {
    const { group } = this.context;
    if (!group || !group.isOwnedByUser(CurrentUser.getUserId())) {
      return null;
    }

    const { message } = this.props;
    const groupThreads = message.getGroupThreads();

    if (groupThreads.length <= 1) {
      return null;
    }

    return (
      <div className="GroupMessageInformation-recipients">
        <span className="GroupMessageInformation-bullet">•</span>
        <LayerTrigger
          layer={this.renderRecipientsDropdown()}
          trigger="click"
          placement="bottom"
          alignment="middle">
          <Button
            className="GroupMessageInformation-recipients-button"
            level="text"
            size="small"
            eventElementName="GroupMessageInformation-recipients-button">
            {this.renderRecipientsLabel()}
          </Button>
        </LayerTrigger>
      </div>
    );
  }

  /**
   * Renders a dropdown containing groups that received or are scheduled to receive the message.
   */
  renderRecipientsDropdown() {
    const { message } = this.props;
    const groupThreads = message.getGroupThreads();

    return (
      <Menu>
        {groupThreads.map(thread => {
          const id = thread.group_id;
          invariant(id, 'renderRecipientsDropdown thread must have a group id');
          const group = GroupStore.getGroup(id);

          if (group) {
            return (
              <MenuItem
                key={id}
                className="GroupMessageInformation-recipient"
                to={`/classes/${group.getCodeOrError()}`}>
                <GroupAvatar className="GroupMessageInformation-recipient-avatar" group={group} />
                <Text className="GroupMessageInformation-recipient-name" truncate>
                  <GroupName group={group} />
                </Text>
              </MenuItem>
            );
          }
          return null;
        })}
      </Menu>
    );
  }

  /**
   * Renders a label containing the number of groups that received or are scheduled to receive
   * the message.
   */
  renderRecipientsLabel() {
    const { message } = this.props;

    const groupCount = message.getGroupThreads().length;
    const subscriberCount = message.getSubscriberThreads().length;
    let label; // FIXME: Sigh. This alone makes me want to do the work involved to get Intl.MessageFormat's // pluralization working with our i18n tool.

    if (message.getStatus() === 'scheduled') {
      label = groupCount === 1 ? 'Sending to 1 class' : `Sending to ${groupCount} classes`;
    } else {
      if (groupCount === 1) {
        if (subscriberCount === 1) {
          label = 'Sent to 1 class and 1 person';
        } else if (subscriberCount > 1) {
          label = `Sent to 1 class and ${subscriberCount} people`;
        } else {
          label = 'Sent to 1 class';
        }
      } else if (groupCount > 1) {
        if (subscriberCount === 1) {
          label = `Sent to ${groupCount} classes and 1 person`;
        } else if (subscriberCount > 1) {
          label = `Sent to ${groupCount} classes and ${subscriberCount} people`;
        } else {
          label = `Sent to ${groupCount} classes`;
        }
      } else {
        if (subscriberCount === 1) {
          label = `Sent to ${subscriberCount} person`;
        } else if (subscriberCount > 1) {
          label = `Sent to ${subscriberCount} people`;
        }
      }
    }
    return label;
  }

  renderTimestamp() {
    const { message } = this.props;
    if (message.getStatus() === 'failed') {
      return (
        <div className="GroupMessageInformation-timestamp">
          <span className="GroupMessageInformation-failed">
            An error has occurred. Please edit your message and send again.
          </span>
        </div>
      );
    }
    let time;
    let date;
    let glyph;
    if (message.getStatus() === 'scheduled') {
      time = <Time date={message.getSendAt()} hour="numeric" minute="2-digit" />;
      date = <Time date={message.getSendAt()} day="numeric" month="long" year="numeric" />;
      glyph = <Glyph className="GroupMessageInformation-clock" name="schedule" size="small" />;
    } else {
      time = <Time date={message.getSentAt()} hour="numeric" minute="2-digit" />;
      date = <Timestamp date={message.getSentAt()} />;
    }
    return (
      <div className="GroupMessageInformation-timestamp">
        {glyph}
        {date}
        {' at '}
        {time}
      </div>
    );
  }
  render() {
    return (
      <div className="GroupMessageInformation">
        {this.renderTimestamp()}
        {CurrentUser.isSignedIn() && this.renderRecipients()}
      </div>
    );
  }
}
